import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, X } from 'lucide-react';
import { onboardingApi } from '@/lib/api';
import { isOnboardingTourActive, stopOnboardingTour } from '@/lib/onboardingTour';
import { mixpanelService } from '@/lib/mixpanelService';
import { notify } from '@/store/notificationStore';
import { useAuthStore } from '@/store/authStore';

interface TourStep {
  route: string;
  selector: string;
  title: string;
  body: string;
  placement: 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    route: '/dashboard',
    selector: '[data-tour="dashboard-overview"]',
    title: 'Begin with the market read',
    body: 'The dashboard is the fastest way to see broad insider buying strength before drilling into individual filings.',
    placement: 'bottom',
  },
  {
    route: '/dashboard',
    selector: '[data-tour="global-search"]',
    title: 'Jump straight to a name',
    body: 'Search takes you directly to companies, insiders, and tickers without walking through tables first.',
    placement: 'bottom',
  },
  {
    route: '/transactions',
    selector: '[data-tour="transactions-nav"]',
    title: 'Use transactions for investigation',
    body: 'The transactions feed is where you filter Form 4 activity by ticker, insider, value, date, and conviction.',
    placement: 'right',
  },
  {
    route: '/dashboard',
    selector: '[data-tour="notables-section"]',
    title: 'Start with curated ideas',
    body: 'Notable stocks surface recent high-skill, high-conviction insider buying, plus names trading below what insiders recently paid.',
    placement: 'bottom',
  },
  {
    route: '/alerts',
    selector: '[data-tour="alerts-nav"]',
    title: 'Save monitoring for later',
    body: 'Alerts turn a company or insider filter into something the platform can monitor for you.',
    placement: 'right',
  },
];

interface AnchorRect {
  height: number;
  left: number;
  top: number;
  width: number;
}

export const GuidedOnboardingTour: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const fetchAndSetUser = useAuthStore((state) => state.fetchAndSetUser);
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const currentStep = TOUR_STEPS[stepIndex];
  const isFinalStep = stepIndex === TOUR_STEPS.length - 1;
  const shouldShow = isActive && Boolean(user);

  useEffect(() => {
    setIsActive(isOnboardingTourActive());
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldShow || !currentStep) return;

    if (location.pathname !== currentStep.route) {
      navigate(currentStep.route, { replace: true });
    }
  }, [currentStep, location.pathname, navigate, shouldShow]);

  useEffect(() => {
    if (!shouldShow || !currentStep) return;

    let frame = 0;

    const updateAnchor = () => {
      const target = document.querySelector(currentStep.selector);
      if (!target) {
        setAnchorRect(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setAnchorRect({
        height: rect.height,
        left: rect.left,
        top: rect.top,
        width: rect.width,
      });
      target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    };

    frame = window.requestAnimationFrame(updateAnchor);
    window.addEventListener('resize', updateAnchor);
    window.addEventListener('scroll', updateAnchor, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateAnchor);
      window.removeEventListener('scroll', updateAnchor, true);
    };
  }, [currentStep, location.pathname, shouldShow]);

  const popoverStyle = useMemo(() => {
    if (!anchorRect) {
      return {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const margin = 16;
    const width = 360;
    const viewportPadding = 16;
    const maxLeft = window.innerWidth - width - viewportPadding;

    if (currentStep.placement === 'right') {
      return {
        left: Math.min(anchorRect.left + anchorRect.width + margin, maxLeft),
        top: Math.max(anchorRect.top, viewportPadding),
      };
    }

    if (currentStep.placement === 'left') {
      return {
        left: Math.max(anchorRect.left - width - margin, viewportPadding),
        top: Math.max(anchorRect.top, viewportPadding),
      };
    }

    return {
      left: Math.min(Math.max(anchorRect.left, viewportPadding), maxLeft),
      top: Math.min(anchorRect.top + anchorRect.height + margin, window.innerHeight - 260),
    };
  }, [anchorRect, currentStep]);

  const completeTour = async () => {
    setIsSaving(true);
    try {
      await onboardingApi.update({ onboarding_completed: true });
      await fetchAndSetUser();
      stopOnboardingTour();
      setIsActive(false);
      mixpanelService.track('Onboarding Tour Completed', {
        steps: TOUR_STEPS.length,
        subscription_tier: user?.subscription?.tier || 'free',
      });
      navigate('/dashboard', { replace: true });
    } catch {
      notify.error('Could not finish tour', 'Please try again before leaving the walkthrough.');
    } finally {
      setIsSaving(false);
    }
  };

  const closeTour = async () => {
    setIsSaving(true);
    try {
      await onboardingApi.update({ onboarding_dismissed: true });
      await fetchAndSetUser();
      stopOnboardingTour();
      setIsActive(false);
      mixpanelService.track('Onboarding Tour Dismissed', {
        step: stepIndex + 1,
        subscription_tier: user?.subscription?.tier || 'free',
      });
    } catch {
      notify.error('Could not close tour', 'Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!shouldShow || !currentStep) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-slate-950/35 backdrop-blur-[1px]" />

      {anchorRect && (
        <div
          className="absolute rounded-lg border-2 border-cyan-400 shadow-[0_0_0_9999px_rgba(2,6,23,0.34),0_0_24px_rgba(34,211,238,0.5)]"
          style={{
            height: anchorRect.height + 12,
            left: anchorRect.left - 6,
            top: anchorRect.top - 6,
            width: anchorRect.width + 12,
          }}
        />
      )}

      <section
        className="pointer-events-auto fixed w-[calc(100vw-32px)] max-w-[360px] rounded-lg border border-v2-border bg-v2-surface p-4 shadow-2xl"
        style={popoverStyle}
        aria-live="polite"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-v2-text-tertiary">
              Tour step {stepIndex + 1} of {TOUR_STEPS.length}
            </p>
            <h2 className="mt-1 text-base font-semibold text-v2-text-primary">
              {currentStep.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeTour}
            disabled={isSaving}
            className="rounded-md p-1.5 text-v2-text-tertiary transition-colors hover:bg-v2-hover-bg hover:text-v2-text-primary disabled:opacity-50"
            aria-label="Close tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm leading-6 text-v2-text-tertiary">{currentStep.body}</p>

        <div className="mt-4 flex items-center gap-1.5">
          {TOUR_STEPS.map((step, index) => (
            <span
              key={step.title}
              className={`h-1.5 rounded-full transition-all ${
                index === stepIndex ? 'w-6 bg-v2-buy' : 'w-1.5 bg-v2-border-hover'
              }`}
            />
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStepIndex((index) => Math.max(index - 1, 0))}
            disabled={stepIndex === 0 || isSaving}
            className="inline-flex h-9 items-center justify-center rounded-md border border-v2-border bg-v2-surface px-3 text-sm font-medium text-v2-text-secondary transition-colors hover:bg-v2-hover-bg disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </button>

          <button
            type="button"
            onClick={() => {
              if (isFinalStep) {
                void completeTour();
                return;
              }
              setStepIndex((index) => index + 1);
            }}
            disabled={isSaving}
            className="inline-flex h-9 items-center justify-center rounded-md bg-v2-text-primary px-3 text-sm font-medium text-v2-bg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving
              </>
            ) : isFinalStep ? (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Finish
              </>
            ) : (
              <>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
};

export default GuidedOnboardingTour;
